'use client';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { EmailOtp } from '~/components/auth/email-otp';
import { Mfa } from '~/components/auth/mfa';
import { SavePropertyForm } from '~/components/parcel-watch/workspace-forms';
import {
  WorkspaceLimitNotice,
  isWorkspaceFull,
} from '~/components/parcel-watch/workspace-limit-notice';
import { usePublicPropertyAuth } from '~/components/parcel-watch/public-property-auth';
import { NativeSelect, NativeSelectOption } from '~/components/ui/native-select';
import { useSaveCompletion } from './save-completion-context';
import { NameStep } from './name-step';
import { Spinner } from '~/components/ui/spinner';
const workspaceSchema = z.discriminatedUnion('mfaRequired', [
  z.object({
    mfaRequired: z.literal(true),
    factors: z.array(z.object({ id: z.string(), friendly_name: z.string().optional() })),
  }),
  z.object({
    mfaRequired: z.literal(false),
    accounts: z.array(
      z.object({
        id: z.uuid(),
        name: z.string(),
        personal: z.boolean(),
        property_count: z.number().int().nullable().optional(),
        property_limit: z.number().int().nullable().optional(),
      }),
    ),
    portfolios: z.array(
      z.object({ id: z.uuid(), account_id: z.uuid(), name: z.string(), is_default: z.boolean() }),
    ),
    needsName: z.boolean().default(false),
  }),
]);
export function SavePropertyOnboarding({
  block,
  lot,
  locale,
  defaultEmail,
  address = '',
  autoSave = true,
}: {
  block: string;
  lot: string;
  locale: string;
  defaultEmail?: string;
  address?: string;
  /** False when the sheet reopened from a stored intent instead of a click. */
  autoSave?: boolean;
}) {
  const auth = usePublicPropertyAuth();
  const [state, setState] = useState({ verified: false, accountId: '' });
  const close = useSaveCompletion();
  const ready = state.verified || auth.isSignedIn;
  const workspaces = useQuery({
    queryKey: ['save-workspaces', ready],
    enabled: Boolean(ready),
    queryFn: async () => {
      const r = await fetch('/api/onboarding/workspaces');
      if (!r.ok) throw new Error();
      return workspaceSchema.parse(await r.json());
    },
    retry: false,
  });
  // Never send a code before knowing the person is signed out.
  if (auth.isSignedIn === undefined && !auth.failed && !state.verified) return <Spinner />;
  if (!ready)
    return (
      <EmailOtp
        defaultEmail={defaultEmail}
        autoSend={Boolean(defaultEmail)}
        onVerified={() => setState((s) => ({ ...s, verified: true }))}
      />
    );
  if (workspaces.isPending) return <Spinner />;
  if (!workspaces.data)
    return <p role="alert">Workspaces are unavailable. Close this sheet and try again.</p>;
  const data = workspaces.data;
  if (data.mfaRequired)
    return (
      <Mfa
        factors={data.factors}
        challenge
        onVerified={() => {
          void workspaces.refetch();
        }}
      />
    );
  if (data.needsName)
    return (
      <NameStep
        onDone={() => {
          void workspaces.refetch();
        }}
      />
    );
  const usageOf = (account: (typeof data.accounts)[number]) =>
    account.property_count != null && account.property_limit != null
      ? {
          account_id: account.id,
          property_count: account.property_count,
          property_limit: account.property_limit,
        }
      : null;
  const open = (account: (typeof data.accounts)[number]) => !isWorkspaceFull(usageOf(account));
  // Prefer the default workspace, then any workspace that still has room.
  const accountId =
    state.accountId ||
    data.accounts.find((a) => a.personal && open(a))?.id ||
    data.accounts.find(open)?.id ||
    data.accounts.find((a) => a.personal)?.id ||
    data.accounts[0]?.id;
  if (!accountId) return <p>You need Member access to a workspace to save a property.</p>;
  const portfolios = data.portfolios.filter((p) => p.account_id === accountId);
  const selected = data.accounts.find((a) => a.id === accountId);
  const selectedUsage = selected ? usageOf(selected) : null;
  return (
    <div className="space-y-5">
      {data.accounts.length > 1 && (
        <label className="grid gap-2 text-sm font-semibold">
          Workspace
          <NativeSelect
            className="w-full"
            value={accountId}
            onChange={(e) => setState((s) => ({ ...s, accountId: e.target.value }))}
          >
            {data.accounts.map((a) => (
              <NativeSelectOption key={a.id} value={a.id}>
                {a.name}
                {open(a) ? '' : ' (full)'}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </label>
      )}
      {isWorkspaceFull(selectedUsage) ? (
        <WorkspaceLimitNotice usage={selectedUsage} />
      ) : (
        <SavePropertyForm
          key={accountId}
          accountId={accountId}
          address={{ address, parcel: { block, lot } }}
          locale={locale}
          portfolios={portfolios}
          onClose={close ?? undefined}
          autoSubmit={autoSave && data.accounts.length === 1 && portfolios.length <= 1}
        />
      )}
    </div>
  );
}
